import OfferSortControl from 'components/Common/OfferSortControl/OfferSortControl'
import TextButton from 'components/Luxkit/Button/TextButton'
import LineMapIcon from 'components/Luxkit/Icons/line/LineMapIcon'
import BodyText from 'components/Luxkit/Typography/BodyText'
import { OfferListSortOption } from 'constants/offerListFilters'
import React, { useCallback } from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import AssistChip from 'components/Luxkit/Chips/AssistChip'
import LineFilterAltIcon from 'components/Luxkit/Icons/line/LineFilterAltIcon'
import OfferListFilterSidebar, { OfferListFilterSubmit } from 'components/OfferList/OfferListFilter/OfferListFilterSidebar'
import useToggle from 'hooks/useToggle'
import { connect } from 'react-redux'
import { getOfferListKeySelector } from 'selectors/offerSelectors'
import { replace } from 'connected-react-router'
import { setSearchParamValue, deleteSearchParams } from 'lib/urlUtils'
import { useAppDispatch } from 'hooks/reduxHooks'
import MobileOfferListFiltersWithoutToggles from 'components/OfferList/OfferListFilter/MobileOfferListFiltersWithoutToggles'
import noop from 'lib/noop'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import config from 'constants/config'
import { searchViewTypeSwitch, searchSwitchViewClick } from 'analytics/eventDefinitions'
import { fireInteractionEvent, addGTMEvent } from 'api/googleTagManager'
import { pluralizeToString } from 'lib/pluralize'

const Container = styled(CSSBreakpoint)`
  display: flex;
  justify-content: space-between;
  width: 100%;
`

const MobileContainer = styled(CSSBreakpoint)`
  width: 100%;
`

const StyledBodyText = styled(BodyText)`
  margin-top: ${rem(24)};
`

const FilterAndCountContainer = styled.div`
  display: flex;
  align-items: center;
  > * + * {
    margin-left: ${rem(16)};
  }
`

const ButtonContainer = styled.div`
  display: flex;
  > * + * {
    margin-left: ${rem(8)};
  }
`

function handleClick() {
  fireInteractionEvent(searchViewTypeSwitch('map'))
  addGTMEvent(searchSwitchViewClick())
}

interface MappedStateProps {
  availableFilters: App.OfferListFilterOptions;
}

interface Props {
  mainListTotal: number;
  sortOptions: Array<OfferListSortOption>;
  mapViewUrl?: string;
  isFlexible?: boolean
  shouldShowFilters?: boolean
  filters: App.OfferListFilters;
  windowSearch: string;
  showOfferCount?: boolean;
  showHolidayTypes?: boolean;
}

const filterToArray = (value?: string | string[]): string[] => {
  if (value) {
    return Array.isArray(value) ? value : [value]
  }
  return []
}

function OfferCountAndSort(props: Props & MappedStateProps) {
  const {
    mainListTotal,
    sortOptions,
    mapViewUrl,
    isFlexible,
    shouldShowFilters = false,
    showOfferCount = true,
    filters,
    windowSearch,
    availableFilters,
    showHolidayTypes,
  } = props
  const countText = isFlexible ? `Showing ${pluralizeToString('offer', mainListTotal)} with cheapest availabilities for your dates` : `Showing ${pluralizeToString('offer', mainListTotal)}`
  const { on: openFilters, off: closeFilters, value: showFilters } = useToggle()

  const dispatch = useAppDispatch()

  const onFilterApply = useCallback((values: OfferListFilterSubmit) => {
    let search = windowSearch

    const locations = filterToArray(values.destinations)
    const propertyTypes = filterToArray(values.propertyTypes)
    const amenities = filterToArray(values.amenities)
    const inclusions = filterToArray(values.inclusions)
    const holidayTypes = filterToArray(values.holidayTypes)
    const offerTypes = filterToArray(values.offerTypes)

    search = setSearchParamValue(search, 'locations', locations.join(','))
    search = setSearchParamValue(search, 'propertyTypes', propertyTypes.join(','))
    search = setSearchParamValue(search, 'amenities', amenities.join(','))
    search = setSearchParamValue(search, 'inclusions', inclusions.join(','))
    search = setSearchParamValue(search, 'holidayTypes', holidayTypes.join(','))
    search = setSearchParamValue(search, 'customerRatingGte', values.customerRatings ?? '')

    if (offerTypes.length > 1) {
      search = deleteSearchParams(search, 'offerType')
    } else if (offerTypes.length === 1) {
      search = setSearchParamValue(search, 'offerType', offerTypes[0])
    }

    if (values.sortBy) {
      if (values.sortBy === 'recommended') {
        search = deleteSearchParams(search, 'sortBy')
      } else {
        search = setSearchParamValue(search, 'sortBy', values.sortBy)
      }
    }

    dispatch(replace({ search }))
    closeFilters()
  }, [closeFilters, dispatch, windowSearch])

  return (<>
    <MobileContainer max="xs">
      <MobileOfferListFiltersWithoutToggles
        mapViewPath={mapViewUrl}
        onSortAndFilterClick={openFilters}
        onViewSwitch={noop}
        hasSortOptions
        showFilterIcon={false}
      />
      {showOfferCount && <StyledBodyText variant="medium">{countText}</StyledBodyText>}
    </MobileContainer>
    <Container min="sm">
      <FilterAndCountContainer>
        {shouldShowFilters && <AssistChip size="medium" variant="filled" startIcon={<LineFilterAltIcon />} onClick={openFilters}>
          Filters
        </AssistChip>}
        {showOfferCount && <BodyText variant="large">{countText}</BodyText>}
      </FilterAndCountContainer>
      <ButtonContainer>
        <OfferSortControl sortOptions={sortOptions} />
        {!config.businessTraveller.isEnabled && mapViewUrl &&
          <TextButton
            kind="secondary"
            variant="dark"
            to={mapViewUrl}
            startIcon={<LineMapIcon />}
            data-testid="map-view-button"
            onClick={handleClick}
          >
            Map view
          </TextButton>}
      </ButtonContainer>
    </Container>
    <OfferListFilterSidebar
      open={showFilters}
      availableFilters={availableFilters?.filters}
      filterOrder={availableFilters?.filterOrder}
      onClose={closeFilters}
      onApply={onFilterApply}
      existingFilters={filters}
      sortOptions={sortOptions}
      isLoading={availableFilters?.fetching}
      showHolidayTypes={showHolidayTypes}
      showOfferTypes
    />
  </>)
}

export default connect<MappedStateProps, undefined, Props, App.State>((appState, ownProps) => {
  const offerListKey = getOfferListKeySelector(appState, ownProps)
  return {
    availableFilters: appState.offer.offerListFilterOptions[offerListKey],
  }
})(OfferCountAndSort)
